import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["table", "portfolioSelect", "programSelect", "phaseSelect", "statusSelect"]

  // ── Filters ────────────────────────────────────────────────────

  submitFilters(event) {
    // Skip for search input (handled by debounceSearch)
    if (event?.target?.name === "search") return
    // Checkbox dropdowns (PMs, statuses) apply via their Apply button.
    if (["pm_id[]", "status[]"].includes(event?.target?.name)) return
    const form = this.element.querySelector("form")
    if (form) form.requestSubmit()
  }

  applyPmFilter() {
    const form = this.element.querySelector("form")
    if (form) form.requestSubmit()
  }

  clearPmFilter() {
    this._clearChecks("pm_id[]")
  }

  clearStatusFilter() {
    this._clearChecks("status[]")
  }

  _clearChecks(name) {
    this.element.querySelectorAll(`input[name="${name}"]`).forEach((box) => { box.checked = false })
    const form = this.element.querySelector("form")
    if (form) form.requestSubmit()
  }

  // ── Portfolio → Program cascade ─────────────────────────────────

  cascadePrograms() {
    if (!this.hasPortfolioSelectTarget || !this.hasProgramSelectTarget) return

    const portfolioId = this.portfolioSelectTarget.value
    const programMap = JSON.parse(this.portfolioSelectTarget.dataset.programMap || "{}")
    const select = this.programSelectTarget
    const currentValue = select.value

    // Clear existing options
    select.innerHTML = '<option value="">All Programs</option>'

    if (portfolioId) {
      // Show only programs for selected portfolio
      const programs = programMap[portfolioId] || []
      programs.forEach(p => {
        const opt = document.createElement("option")
        opt.value = p.id
        opt.textContent = p.name
        if (String(p.id) === currentValue) opt.selected = true
        select.appendChild(opt)
      })
    } else {
      // Show all programs across all portfolios
      Object.values(programMap).flat().forEach(p => {
        const opt = document.createElement("option")
        opt.value = p.id
        opt.textContent = p.name
        if (String(p.id) === currentValue) opt.selected = true
        select.appendChild(opt)
      })
    }

    // If the previously selected program is no longer in the list, reset it
    if (select.value !== currentValue) {
      select.value = ""
    }
  }

  // ── Phase → Status cascade ──────────────────────────────────────

  cascadeStatuses() {
    if (!this.hasPhaseSelectTarget || !this.hasStatusSelectTarget) return

    const phase = this.phaseSelectTarget.value
    const statusMap = JSON.parse(this.phaseSelectTarget.dataset.statusMap || "{}")
    const select = this.statusSelectTarget
    const currentValue = select.value

    select.innerHTML = '<option value="">All Statuses</option>'

    let statuses
    if (phase && statusMap[phase]) {
      statuses = statusMap[phase]
    } else {
      // All statuses across all phases, deduplicated by value
      const seen = new Set()
      statuses = []
      Object.values(statusMap).flat().forEach(s => {
        if (!seen.has(s[0])) {
          seen.add(s[0])
          statuses.push(s)
        }
      })
    }

    statuses.forEach(([val, label]) => {
      const opt = document.createElement("option")
      opt.value = val
      opt.textContent = label
      if (val === currentValue) opt.selected = true
      select.appendChild(opt)
    })

    if (select.value !== currentValue) {
      select.value = ""
    }
  }

  // ── Sorting ────────────────────────────────────────────────────

  sort(event) {
    const key = event.currentTarget.dataset.sortKey
    if (!key || !this.hasTableTarget) return

    const tbody = this.tableTarget.querySelector("tbody")
    if (!tbody) return

    const rows = Array.from(tbody.querySelectorAll("tr"))
    const currentDir = this.tableTarget.dataset.sortDir
    const currentKey = this.tableTarget.dataset.sortKey

    // Toggle direction if same column, otherwise default desc for rag, asc for others
    let dir
    if (currentKey === key) {
      dir = currentDir === "asc" ? "desc" : "asc"
    } else {
      dir = key === "rag" ? "desc" : "asc"
    }

    rows.sort((a, b) => {
      let aVal, bVal

      if (key === "rag") {
        aVal = parseInt(a.dataset.ragOrder || "3", 10)
        bVal = parseInt(b.dataset.ragOrder || "3", 10)
      } else if (key === "name" || key === "pm" || key === "phase") {
        aVal = a.dataset[`sort${this._capitalize(key)}`] || ""
        bVal = b.dataset[`sort${this._capitalize(key)}`] || ""
        return dir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      } else {
        const attr = `sort${this._capitalize(key)}`
        aVal = parseFloat(a.dataset[attr] || "0")
        bVal = parseFloat(b.dataset[attr] || "0")
      }

      if (aVal < bVal) return dir === "asc" ? -1 : 1
      if (aVal > bVal) return dir === "asc" ? 1 : -1
      return 0
    })

    rows.forEach(row => tbody.appendChild(row))
    this.tableTarget.dataset.sortKey = key
    this.tableTarget.dataset.sortDir = dir
  }

  _capitalize(str) {
    // Convert sort key like "last_activity" to "LastActivity" for dataset access
    return str.replace(/(^|_)([a-z])/g, (_, _sep, c) => c.toUpperCase())
  }

  // ── Row navigation ─────────────────────────────────────────────

  navigateToProject(event) {
    // Don't hijack clicks on links
    if (event.target.closest("a")) return
    const url = event.currentTarget.dataset.url
    if (url) window.location.href = url
  }
}
